import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, Check, AlertCircle, Loader2 } from 'lucide-react';
import api from '@utils/api';
import { useAuth } from '@contexts/AuthContext';
import { validatePasswordChange, MIN_LENGTH } from '@utils/passwordPolicy';

const labelCls = 'text-2xs font-mono uppercase tracking-widest text-gl-muted';
const inputCls =
  'w-full px-3 py-2 rounded-lg text-sm text-gl-ghost placeholder-gl-muted bg-gl-surface border border-gl-border focus:outline-none focus-visible:ring-2 focus-visible:ring-gl-ghost';

const REDIRECT_MS = 2500;

/** "Security" tab of My Account: change your own password. */
export function SecurityTab() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const invalid = validatePasswordChange(form);
    if (invalid) { setError(invalid); return; }
    setSaving(true);
    setError(null);
    try {
      await api.post('/auth/change-password', { currentPassword: form.current, newPassword: form.next });
      setForm({ current: '', next: '', confirm: '' });
      setDone(true);
      // The server has revoked every session, this one included: sign out cleanly and go to login.
      timerRef.current = setTimeout(async () => {
        await logout();
        navigate('/login', { replace: true });
      }, REDIRECT_MS);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Could not change the password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-2xl p-6 space-y-4"
      style={{
        background:     'rgba(10,16,35,0.9)',
        border:         '1px solid rgba(255,255,255,0.07)',
        backdropFilter: 'blur(24px)',
        boxShadow:      '0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(45,125,70,0.06)',
      }}
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(45,125,70,0.08)', color: '#2D7D46' }}>
          <KeyRound size={14} />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-display font-semibold text-gl-ghost">Change password</h3>
          <p className="text-xs text-gl-muted mt-1">
            Use at least {MIN_LENGTH} characters. After the change you are signed out everywhere and sign in again with the new password.
          </p>
        </div>
      </div>

      {done ? (
        <div role="status" className="flex items-center gap-2 p-3 rounded-lg text-xs" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', color: '#22C55E' }}>
          <Check size={12} className="shrink-0" />
          Password changed. Taking you to the sign-in page…
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3 max-w-sm">
          {error && (
            <div role="alert" className="flex items-center gap-2 p-3 rounded-lg text-xs" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#EF4444' }}>
              <AlertCircle size={12} className="shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label htmlFor="pw-current" className={labelCls}>Current password</label>
            <input id="pw-current" name="current-password" type="password" autoComplete="current-password"
              className={inputCls} value={form.current} onChange={set('current')} required />
          </div>
          <div>
            <label htmlFor="pw-new" className={labelCls}>New password</label>
            <input id="pw-new" name="new-password" type="password" autoComplete="new-password"
              className={inputCls} value={form.next} onChange={set('next')} minLength={MIN_LENGTH} required />
          </div>
          <div>
            <label htmlFor="pw-confirm" className={labelCls}>Confirm new password</label>
            <input id="pw-confirm" name="confirm-password" type="password" autoComplete="new-password"
              className={inputCls} value={form.confirm} onChange={set('confirm')} required />
          </div>

          <button type="submit" disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-2xs font-mono uppercase tracking-wider disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #2D7D46, #1A5E9E)', color: '#0D1B2A' }}>
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            Change password
          </button>
        </form>
      )}
    </div>
  );
}
